import { pageTransition } from '../modules/transitions/pageTransitions'
import { isTouchDevice, setLoading } from '../utils'
import AnchorLinks from '../modules/anchorLinks'
import { app_env, appSettings } from '../context'

class Router {
    // Dom selectors
    wrapperSelector = '[data-content-wrapper]'
    contentSelector = '[data-content]'
    dataSelector = '#page-data'

    // Settings
    cache = false

    // Data
    links: Array<{ el: HTMLAnchorElement }> = []
    cacheContents: Map<string, Element | null> = new Map()
    cacheAbortController: AbortController | null = null

    main: Element | null = null
    popstate: boolean = false

    constructor() {
        this.main = document.querySelector(this.wrapperSelector)
        this.popstate = false
    }

    // Init
    init(options: { cache?: boolean } = {}) {
        this.cache = options.cache || false
        
        this.setLinks()
        AnchorLinks.init()

        window.addEventListener('popstate', this.onBrowserBack, false)
    }

    // Set links
    setLinks(container: Document | Element = document) {
        this.links = []
        container.querySelectorAll('a').forEach(element => {
            const el = element as HTMLAnchorElement
            if (
                el.origin === window.location.origin
                &&
                !el.matches('[target="_blank"]')
                &&
                !el.matches('[href^="#"]')
                &&
                !el.matches('[href^="mailto:"]')
                &&
                !el.matches('[href^="tel:"]')
                &&
                !el.matches('.no-router')
                &&
                !/\/wp-(admin|login|content|includes|json)\b/.test(el.pathname)
            ) {
                this.links.push({
                    el: el,
                })
            }
        })
        
        // Setup links listener
        this.setupLinkListeners()
        
        // Cache links initialization
        if (this.cache) {
            this.cacheLinksInit()
        }
    }

    abortCache() {
        if (this.cacheAbortController) {
            this.cacheAbortController.abort()
            this.cacheAbortController = null
        }
    }

    // Cache visible links first, then the rest
    async cacheLinksInit() {
        this.abortCache()
        this.cacheAbortController = new AbortController()
        const signal = this.cacheAbortController.signal

        const visibleLinks = await this.getVisibleLinks()
        
        if (app_env === 'development') console.log(`Starting cache: ${this.links.length} total links, ${visibleLinks.length} visible`)
        
        await this.cacheLinks(visibleLinks, signal)
        if (signal.aborted) return

        const remainingLinks = this.links.filter(link => {
            return !visibleLinks.includes(link) && !this.cacheContents.has(link.el.href)
        })
        
        if (remainingLinks.length > 0) {
            await this.cacheLinks(remainingLinks, signal)
        }

        if (app_env === 'development') console.log('Fetch done')
    }

    // Cache links sequentially (abortable between each fetch)
    async cacheLinks(links: Array<{ el: HTMLAnchorElement }>, signal?: AbortSignal) {
        if (!links) return

        for (const link of links) {
            if (signal && signal.aborted) return

            const url = link.el.href
            if (this.cacheContents.has(url)) continue

            try {
                const content = await this.fetchContent(url, signal)
                this.cacheContents.set(url, content)
            } catch (error) {
                if (signal && (signal as any).aborted) return
                console.error(`Error fetching ${url}:`, error)
                this.cacheContents.set(url, null)
            }
        }
    }

    // Get visible links
    getVisibleLinks(): Promise<Array<{ el: HTMLAnchorElement }>> {
        return new Promise((resolve) => {
            const visibleLinks: Array<{ el: HTMLAnchorElement }> = []
            let observedCount = 0
            
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const link = this.links.find(l => l.el === entry.target)
                        if (link && !visibleLinks.includes(link)) {
                            visibleLinks.push(link)
                        }
                    }
                    observedCount++
                    
                    if (observedCount === this.links.length) {
                        observer.disconnect()
                        resolve(visibleLinks)
                    }
                })
            }, {
                root: null,
                rootMargin: '50px',
                threshold: 0.1
            })
            
            this.links.forEach(link => {
                observer.observe(link.el)
            })
            
            if (this.links.length === 0) {
                resolve([])
            }
        })
    }

    // Setup links listener
    setupLinkListeners() {
        for (const link of this.links) {
            link.el.addEventListener('click', (e) => {
                e.preventDefault()
                this.onLinkClick(link)
            }, false)
        }
    }

    // On links click
    onLinkClick(link: { el: HTMLAnchorElement }) {
        // If transition active return
        if (document.body.classList.contains('on-transition')) return

        this.abortCache()

        let oldContent = document.querySelector(this.contentSelector)
        let newUrl = link.el.href

        // Dispatch event linkClick
        document.dispatchEvent(new CustomEvent('linkClick', { detail: {
            link: link.el,
            container: oldContent,
        }} as any))

        // Replace content
        this.replaceContent(newUrl)
    }

    // On browser back (popstate)
    onBrowserBack = () => {
        this.popstate = true
        const newUrl = location.href

        // Replace content
        this.replaceContent(newUrl)
    }

    // Replace content (page transition)
    async replaceContent(newUrl: string, content: Element | null = null) {
        document.body.classList.add('on-transition')
        document.dispatchEvent(new CustomEvent('beforeLeave'))
        setLoading(true)

        let oldContent = document.querySelector(this.contentSelector)

        // Exit animation and content fetch run in parallel
        const exitComplete = new Promise<void>(resolve => {
            appSettings.pageTransitionEnabled ? pageTransition.exit(document, resolve) : resolve()
        })

        let newContent = content
        let cacheContent = this.cacheContents.has(newUrl) ? this.cacheContents.get(newUrl) : null
        
        if (!newContent) {
            if (cacheContent) {
                newContent = cacheContent.cloneNode(true) as Element
            } else {
                newContent = await this.fetchContent(newUrl)
                if (newContent) this.cacheContents.set(newUrl, newContent.cloneNode(true) as Element)
            }
        }

        await exitComplete
        setLoading(false)

        // Swap content while fader covers the screen
        if (this.main && newContent) this.main.appendChild(newContent)
        const newFader = newContent?.querySelector('.hg-fader') as HTMLElement
        if (newFader) newFader.style.visibility = 'visible'
        if (oldContent && oldContent.parentNode) oldContent.parentNode.removeChild(oldContent)

        // Set links
        if (newContent) this.setLinks(newContent)

        // Anchor links
        if (newContent) AnchorLinks.update(newContent)

        // Update new data
        if (newContent) this.updateData(newUrl, newContent)

        // newContentLoad triggers enter animation + afterEnter in app
        if (newContent) document.dispatchEvent(new CustomEvent('newContentLoad', { detail: { 
            container: newContent,
        }} as any))

        this.popstate = false
    }

    // Fetch content from url
    async fetchContent(url: string, signal?: AbortSignal): Promise<Element | null> {
        try {
            let response = await fetch(url, { signal })
            let text = await response.text()

            const wrapper = document.createElement('div')
            wrapper.innerHTML = text

            return wrapper.querySelector(this.contentSelector)

        } catch(error: any) {
            if (error.name === 'AbortError') throw error
            console.error(error.message)
            return null
        }
    }

    // Update new data
    updateData(newUrl: string, newContent: Element) {
        // Update title
        const dataElement = newContent.querySelector(this.dataSelector)
        const title = dataElement ? ((dataElement as HTMLElement).dataset.metaTitle || document.title) : document.title
        document.title = title

        // Update menu active item
        const currentActiveItem = document.querySelector('.menu-item.current-menu-item')
        if (currentActiveItem) currentActiveItem.classList.remove('current-menu-item')
        const nextActiveItem = document.querySelector(`.menu-item > a[href$="${newUrl}"]`)
        if (nextActiveItem) nextActiveItem.closest('.menu-item')!.classList.add('current-menu-item')
        
        // Update body classes
        const bodyClasses = dataElement ? dataElement.getAttribute('class') : ''
        if (bodyClasses) document.body.setAttribute('class', bodyClasses)
        
        if (isTouchDevice) {
            document.body.classList.add('is-touch-device')
        }

        // Update body dataset
        const bodyDataset = document.body.dataset
        for(const data in bodyDataset) {
            if (dataElement && (dataElement as HTMLElement).dataset[data]) {
                bodyDataset[data] = (dataElement as HTMLElement).dataset[data] as string
            }
        }

        // Update metas
        const meta: any = {}
        meta.og = {}

        const createMetaElement = (metaName: string, attribute: string, content: string) => {
            const element = document.createElement('meta')  
            element.setAttribute(attribute, metaName)  
            element.content = content  
            document.getElementsByTagName('head')[0].appendChild(element)
        }
        meta.description = document.querySelector('meta[name="description"]')
        meta.og.title = document.querySelector('meta[property="og:title"]')
        meta.og.description = document.querySelector('meta[property="og:description"]')
        meta.og.url = document.querySelector('meta[property="og:url"]')
        
        // Update meta og:title
        if (meta.og.title) {
            meta.og.title.setAttribute('content', title)
        } else {
            createMetaElement('og:title', 'property', title)
        }
        
        // Update meta description
        const description = dataElement ? (dataElement as HTMLElement).dataset.metaDescription || '' : ''
        if (meta.description) {
            meta.description.setAttribute('content', description)
        } else {
            createMetaElement('description', 'name', description)
        }

        // Update meta og:description
        if (meta.og.description) {
            meta.og.description.setAttribute('content', description)
        } else {
            createMetaElement('og:description', 'property', description)
        }

        // Update meta og:url
        if (meta.og.url) {
            meta.og.url.setAttribute('content', document.location.toString())
        } else {
            createMetaElement('og:url', 'property', document.location.toString())
        }

        // Update browser history
        this.updateHistory(title, newUrl)
    }

    // Update browser history
    updateHistory(title:string,url:string) {
        if (this.popstate) {
            history.replaceState(null, title, url)
        } else {
            history.pushState(null, title, url)
        }
    }
}

export default new Router()

